import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent, Typography, Box } from '@mui/material';
import { Button } from 'system/atoms';
import { Checkbox } from 'common/form/components';
import { useRecoilState } from 'recoil';
import { campaignLaunchConfirmAtom } from 'modules/campaigns/stores/atom';
import { launchModalsSx } from '../styles';

const DontShowAgainCheckbox = () => {
  const [dontShowAgain, setDontShowAgain] = useRecoilState(
    campaignLaunchConfirmAtom
  );

  return (
    <Checkbox
      field={{
        checkboxLabel: "Don't show again"
      }}
      value={dontShowAgain}
      onChange={() => setDontShowAgain(!dontShowAgain)}
    />
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
};

export const InitiateLaunchConfirmModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const [dontShowAgain] = useRecoilState(campaignLaunchConfirmAtom);
  const wasOpenRef = useRef(false);

  // Check if user previously selected "Don't show this again" - only on modal open
  useEffect(() => {
    // Only auto-confirm when modal is opening (not when checkbox changes)
    if (isOpen && !wasOpenRef.current && dontShowAgain) {
      onConfirm();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, dontShowAgain, onConfirm]);

  // Don't render if user chose not to show again
  if (dontShowAgain && !isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={isLoading ? undefined : onClose}>
      <DialogContent sx={launchModalsSx.initiateLaunchModalContent}>
        <Typography
          sx={launchModalsSx.modalTitle}
          data-testid="initiate-launch-modal-title"
        >
          Launch campaign
        </Typography>
        <Typography sx={launchModalsSx.modalSubtitle}>
          Questions, opening messages and recipients will no longer be editable.
          Do you want to continue?
        </Typography>
        <Box sx={launchModalsSx.buttonGroup}>
          <Button
            variant="primary"
            sx={launchModalsSx.primaryButton}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Yes'}
          </Button>
          <Button
            variant="tertiary"
            sx={launchModalsSx.textButton}
            onClick={onClose}
            disabled={isLoading}
          >
            No
          </Button>
        </Box>
        <Box sx={launchModalsSx.dontShowAgainCheckbox}>
          <DontShowAgainCheckbox />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
